import React, { useState, useRef, useEffect, useCallback } from "react";
import { IconButton, Typography } from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import StopIcon from "@mui/icons-material/Stop";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  promptOverridesState,
  selectedPromptIdState,
  inputModeState,
  wsReadyState,
} from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import useNativeInputBridge from "./native-input-bridge";
import {
  convertTextWithXmlTagsToHtml,
  MentionContentEditable,
} from "../common/mention-util";
import PromptDisplay from "./PromptDisplay";
import CloseIcon from "@mui/icons-material/Close";
import VoiceInputComponent from "./voice-input-component";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

// CompactContainer component
const CompactContainer = ({
  children,
  getComponentBgColor,
  style,
  ...props
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: "16px",
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        minHeight: 40,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// CompactTextInput component
const CompactTextInput = ({ children, style, ...props }) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 36,
        maxHeight: 120,
        borderRadius: "16px",
        padding: "8px 12px",
        fontSize: "14px",
        cursor: "text",
        backgroundColor:
          focused || hovered ? "hsl(var(--accent))" : "hsl(var(--muted))",
        border: "none",
        outline: "none",
        display: "flex",
        alignItems: "flex-start",
        overflow: "auto",
        position: "relative",
        transition: "background-color 0.2s",
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...props}
    >
      {children}
    </div>
  );
};

// StopButton component
const StopButton = ({ children, onClick, style, ...props }) => {
  return (
    <IconButton
      onClick={onClick}
      style={{
        flex: 1,
        height: 36,
        borderRadius: "16px",
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
        ...style,
      }}
      {...props}
    >
      {children}
    </IconButton>
  );
};

const InstructionInputCompact = ({
  placeholder = "Type a message...",
  onSendMessage,
  onStop,
  currentContent = "",
  isGenerating = false,
  isError = false,
  // Native bridge props
  prompts = [],
  dataFetchers = {},
}) => {
  // Voice recording state - using global state with localStorage persistence
  const [mode, setMode] = useRecoilState(inputModeState);
  // Add state for draft text from native input
  const [draftText, setDraftText] = useState("");

  // Prompt-related state
  const promptOverrides = useRecoilValue(promptOverridesState);
  const [selectedPromptId, setSelectedPromptId] = useRecoilState(
    selectedPromptIdState
  );
  const [promptsList, setPromptsList] = useState([]);
  const [promptDisplay, setPromptDisplay] = useState(null);
  const prompt = useRef(null);

  const wsReady = useRecoilValue(wsReadyState);
  const getComponentBgColor = useComponentBackgroundColor();
  const appInterface = useAppInterface();

  // Combined disabled state that includes websocket connectivity
  const isInputDisabled = isError || !wsReady;

  // Native bridge for Android
  const nativeBridge = useNativeInputBridge({
    prompts,
    placeholder,
    dataFetchers,
    appInterface,
    onSendMessage: async (message, mentions) => {
      if (onSendMessage) {
        let instruction = message;
        if (prompt.current && prompt.current.title) {
          instruction = `${prompt.current.title}\n${instruction}`;
        }
        await onSendMessage(instruction, null, mentions);
      }
      // Clear draft and prompt after sending
      setDraftText("");
      prompt.current = null;
      setPromptDisplay(null);
    },
    onContentSync: (content, promptData = {}) => {
      // Sync draft text, mentions, and prompt from native input
      setDraftText(content);
      if (promptData) {
        setPromptDisplay(promptData?.title || null);
        prompt.current = promptData;
      }
    },
  });

  // Handle prompt selection
  useEffect(() => {
    if (selectedPromptId) {
      const selected = promptsList.find((p) => p.id === selectedPromptId);
      if (selected) {
        prompt.current = selected;
        setPromptDisplay(selected.title);
        setSelectedPromptId(null);
      }
    }
  }, [selectedPromptId, promptsList, setSelectedPromptId]);

  // Update prompts list when promptOverrides change
  useEffect(() => {
    setPromptsList(promptOverrides);

    if (prompt.current) {
      const newPrompt = promptOverrides.find((p) => p.id === prompt.current.id);
      if (newPrompt) {
        prompt.current = newPrompt;
        setPromptDisplay(newPrompt.title);
      }
    }
  }, [promptOverrides]);

  const handleVoiceResult = async (instruction) => {
    if (onSendMessage) {
      await onSendMessage(instruction, null, {});
    }
    // Clear any draft text, mentions, and prompt after sending voice message
    setDraftText("");
    prompt.current = null;
    setPromptDisplay(null);
  };

  const handleTextClick = () => {
    if (nativeBridge.isNativeSupported) {
      // Sync current text, mentions, and prompt to native bridge before showing
      nativeBridge.showNativeInput(contentToShow, prompt.current);
    }
  };

  const toggleMode = () => {
    setMode(mode === "text" ? "voice" : "text");
  };

  // Use draft text from native input if available, otherwise use currentContent
  const contentToShow = draftText || currentContent;

  // Get HTML content for display
  const htmlContent = convertTextWithXmlTagsToHtml(contentToShow);

  const getPlaceholderText = () => {
    if (isGenerating) return "Generating...";
    if (isInputDisabled) {
      if (!wsReady) return "Connecting...";
      return "Assistant error. Please start a new chat.";
    }
    return placeholder;
  };

  return (
    <div>
      {/* Prompt Display */}
      {promptDisplay && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "6px 12px",
            borderRadius: "16px 16px 0px 0px",
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            borderBottom: "1px solid hsl(var(--border))",
            gap: "8px",
            marginBottom: 0,
          }}
        >
          <Typography
            variant="body2"
            style={{
              fontWeight: 400,
              flexGrow: 1,
              color: "hsl(var(--muted-foreground))",
              fontSize: "12px",
            }}
            component="div"
          >
            <PromptDisplay text={promptDisplay} />
          </Typography>
          <IconButton
            onClick={() => {
              prompt.current = null;
              setPromptDisplay(null);
            }}
            size="small"
            style={{ padding: "2px", width: "20px", height: "20px" }}
          >
            <CloseIcon style={{ fontSize: "12px" }} />
          </IconButton>
        </div>
      )}

      <CompactContainer
        style={{
          borderRadius: promptDisplay ? "0px 0px 16px 16px" : "16px",
          borderTop: promptDisplay ? "none" : "1px solid hsl(var(--border))",
        }}
        getComponentBgColor={getComponentBgColor}
      >
        {/* Show stop button when generating */}
        {isGenerating ? (
          <StopButton onClick={onStop}>
            <StopIcon />
            <Typography
              variant="body2"
              style={{ fontSize: "12px", marginLeft: "8px" }}
            >
              Stop
            </Typography>
          </StopButton>
        ) : (
          <>
            {/* Voice Input Component */}
            <VoiceInputComponent
              mode={mode}
              onModeToggle={toggleMode}
              onVoiceResult={handleVoiceResult}
              isInputDisabled={isInputDisabled}
              variant="compact"
              prompt={prompt.current}
              showModeButton={true}
              isGenerating={isGenerating}
              onStop={onStop}
            />

            {/* Text mode */}
            {mode === "text" && (
              <CompactTextInput>
                <MentionContentEditable
                  html={htmlContent || ""}
                  disabled={isInputDisabled}
                  onChange={() => {}} // No-op since we handle this differently
                  onClick={handleTextClick}
                  onFocus={(e) => e.target.blur()} // Prevent focusing
                  onPaste={(e) => e.preventDefault()} // Prevent pasting
                  onKeyDown={(e) => e.preventDefault()} // Prevent keyboard input
                  tabIndex={-1} // Remove from tab navigation
                  contentEditable={false}
                  style={{
                    width: "100%",
                    lineHeight: 1.4,
                    display: "flex",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    minHeight: "40px",
                    color: htmlContent
                      ? "hsl(var(--foreground))"
                      : "hsl(var(--muted-foreground))",
                  }}
                />
                {!htmlContent && (
                  <Typography
                    variant="body2"
                    style={{
                      position: "absolute",
                      fontSize: "14px",
                      color: "hsl(var(--muted-foreground))",
                      opacity: 0.7,
                      pointerEvents: "none",
                      left: "12px",
                    }}
                  >
                    {getPlaceholderText()}
                  </Typography>
                )}
              </CompactTextInput>
            )}
          </>
        )}
      </CompactContainer>
    </div>
  );
};

export default InstructionInputCompact;
